import React, { useState } from 'react';
import {
  Button,
  DataTable,
  Loading,
  Tag,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { Add, Edit, TrashCan, PinFilled } from '@carbon/react/icons';

import {
  useGetNewsAdminQuery,
  useDeleteNewsMutation,
} from '@/redux/new_services/news';

import NewsForm from './NewsForm';

import moment from 'moment';
import 'moment/locale/de';
moment.locale('de');

// NewsAdminPage lists every news entry incl. drafts/expired and exposes
// create / edit / delete via a single shared modal.
const NewsAdminPage = () => {
  const { data: news = [], isLoading } = useGetNewsAdminQuery();
  const [deleteNews] = useDeleteNewsMutation();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (n) => {
    setEditing(n);
    setFormOpen(true);
  };

  const handleDelete = async (n) => {
    if (!confirm(`Nachricht "${n.title}" wirklich löschen?`)) return;
    try {
      await deleteNews(n.model.ID).unwrap();
    } catch (e) {
      alert('Löschen fehlgeschlagen');
    }
  };

  const headers = [
    { key: 'teaser', header: '' },
    { key: 'title', header: 'Titel' },
    { key: 'status', header: 'Status' },
    { key: 'category', header: 'Kategorie' },
    { key: 'groups', header: 'Sichtbar für' },
    { key: 'published', header: 'Veröffentlicht' },
    { key: 'actions', header: '' },
  ];

  const statusFor = (n) => {
    const now = Date.now();
    const pub = n.published_at ? new Date(n.published_at).getTime() : null;
    const exp = n.expires_at ? new Date(n.expires_at).getTime() : null;
    if (!pub) return { label: 'Entwurf', type: 'cool-gray' };
    if (pub > now) return { label: 'Geplant', type: 'purple' };
    if (exp && exp <= now) return { label: 'Abgelaufen', type: 'warm-gray' };
    return { label: 'Veröffentlicht', type: 'green' };
  };

  return (
    <div className="news-admin-page">
      <div className="news-admin-page__head">
        <h2>Nachrichten verwalten</h2>
        <Button renderIcon={Add} onClick={openNew}>
          Neue Nachricht
        </Button>
      </div>

      {isLoading && <Loading active small withOverlay={false} />}

      <DataTable rows={news.map((n) => ({ id: String(n.model.ID), raw: n }))} headers={headers}>
        {() => (
          <TableContainer>
            <TableToolbar>
              <TableToolbarContent />
            </TableToolbar>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableHeader key={h.key}>{h.header}</TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {news.map((n) => {
                  const status = statusFor(n);
                  const teaserSrc =
                    n.teaser_image?.s3_thumb_url ||
                    n.teaser_image?.s3_med_url ||
                    n.teaser_image?.s3_url ||
                    null;
                  return (
                    <TableRow key={n.model.ID}>
                      <TableCell>
                        {teaserSrc ? (
                          <img
                            src={teaserSrc}
                            alt=""
                            style={{
                              width: 64,
                              height: 48,
                              objectFit: 'cover',
                              display: 'block',
                              borderRadius: 2,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 64,
                              height: 48,
                              background: '#f4f4f4',
                              borderRadius: 2,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {n.is_pinned && <PinFilled size={14} />}
                          <strong>{n.title}</strong>
                        </div>
                        {n.excerpt && (
                          <div
                            style={{
                              color: 'var(--cds-text-secondary)',
                              fontSize: '0.75rem',
                              marginTop: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: '32rem',
                            }}
                          >
                            {n.excerpt}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tag type={status.type}>{status.label}</Tag>
                      </TableCell>
                      <TableCell>{n.category || '—'}</TableCell>
                      <TableCell>
                        {n.groups?.length > 0
                          ? n.groups.map((g) => (
                            <Tag key={g.model.ID} type="cool-gray">
                              {g.name}
                            </Tag>
                          ))
                          : <Tag type="green">Alle</Tag>}
                      </TableCell>
                      <TableCell>
                        {n.published_at ? moment(n.published_at).format('LL') : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={Edit}
                          hasIconOnly
                          iconDescription="Bearbeiten"
                          onClick={() => openEdit(n)}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={TrashCan}
                          hasIconOnly
                          iconDescription="Löschen"
                          onClick={() => handleDelete(n)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>

      <NewsForm open={formOpen} news={editing} onClose={() => setFormOpen(false)} />
    </div>
  );
};

export default NewsAdminPage;
